import { appConfig } from '@ami/react/core';
import { OrderInput } from '@ami/react/shared';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { findDOMNode } from 'react-dom';
import { ProductConfiguration } from '../../products/productConfiguration';
import QuoteWizardContext from '../../quotes/QuoteWizardContext';
import { QuoteProductDimension, QuoteProductDto, couplerDecision } from '../../quotes/otherQuoteEntities';
import { Quote } from '../../quotes/quote';
import { QuoteProductConfiguration } from '../../quotes/quoteProductConfiguration';
import { updateQuotePlusLeadTimesAndProducts } from '../../quotes/quoteUpdater';
import InnerStepHeader from '../InnerStepHeader';
import { quoteProductType } from '../otherQuoteEntities';
import { AttachmentPointSelectionComponent as AttachmentPointSelection } from './AttachmentPointSelectionComponent';
import MachineInformationContainer from './MachineInformationContainer';
import ProductAndOptionsSelectionComponent from './ProductAndOptionsSelectionComponent';
import { ProductCategorySelectionComponent } from './ProductCategorySelectionComponent';
import { ProductGroupSelectionComponent } from './ProductGroupSelectionComponent';
import ProductTypeSelectionComponent from './ProductTypeSelectionComponent';
import { InventoryItemTable } from './components/InventoryItemTable';
import { QuoteInventoryItemViewModel } from './components/QuoteInventoryItemViewModel';
import { ProductGroupTypes } from '../../products/productGroup';

export default class CreateQuoteStep3Component extends React.PureComponent {
  static propTypes = {
    contact: PropTypes.instanceOf(Object),
    quoteProductDto: PropTypes.instanceOf(QuoteProductDto).isRequired,
    submitting: PropTypes.bool.isRequired,
    productConfiguration: PropTypes.instanceOf(ProductConfiguration).isRequired,
    quoteProductConfiguration: PropTypes.instanceOf(QuoteProductConfiguration).isRequired,
    couplerParNumberMade: PropTypes.bool.isRequired,
    onQuoteProductDtoChange: PropTypes.func.isRequired,
    onSaveProduct: PropTypes.func.isRequired,
    onContinue: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    isEditProductMode: PropTypes.bool.isRequired,
    preparingForReview: PropTypes.bool.isRequired,
    isOrderFormMode: PropTypes.bool,
    isAmiPriceLookupMode: PropTypes.bool.isRequired,
    hasAgreedStockTerms: PropTypes.bool.isRequired,
    onHasAgreedStockTermsChange: PropTypes.func.isRequired,
    quote: PropTypes.instanceOf(Quote).isRequired,
    documents: PropTypes.array,
    onloadSharedDocuments: PropTypes.func.isRequired,
    showPricingSpinner: PropTypes.bool.isRequired,
    showLeadTimeCalculationSpinner: PropTypes.bool.isRequired,
    onProductGroupSelectionChange: PropTypes.func.isRequired,
    selectedQuoteProductType: PropTypes.number.isRequired,
    onQuoteUpdated: PropTypes.func.isRequired,
    onOpenEdit: PropTypes.func.isRequired,
    order: PropTypes.instanceOf(OrderInput),
    productSelected: PropTypes.func.isRequired
  };

  state = {
    productId: null,
    inventoryItemId: null,
    selectedInventoryItem: null,
    incompatibleStockItems: [],
    highlightedInventoryItemId: null,
    highlightedInventoryItem: null
  };


  static contextType = QuoteWizardContext;

  constructor(props) {
    super(props);
    this.refToProductGroupSelectionComponent = React.createRef();
    this.refToProductAndOptionsSelectionComponent = React.createRef();
    this.refToProductTypeSelectionComponent = React.createRef();
  }

  componentDidMount() {
    const { quoteProductDto, quoteProductConfiguration } = this.props;
    const inventoryItems = (quoteProductConfiguration.getAllInventoryItems() || [])
      .filter(x => !quoteProductDto.attachmentPointId || x.attachmentPointId === quoteProductDto.attachmentPointId)
      .map((x) => new QuoteInventoryItemViewModel(x));

    this.setState({ inventoryItems: inventoryItems });

    if (window.inventoryQuoteDataViewModel) {
      this.setState({
        highlightedInventoryItemId: window.inventoryQuoteDataViewModel.selectedInventoryItemId || null,
        highlightedInventoryItem: inventoryItems.find(x => x.id === window.inventoryQuoteDataViewModel.selectedInventoryItemId) || null
      });
      this.props.onProductGroupSelectionChange(quoteProductType.inventoryOnly);
      window.inventoryQuoteDataViewModel = undefined;
    }
  }

  getSelectedProduct = (productId) =>
    this.props.quoteProductConfiguration.products.find((x) => x.id === productId);

  getProductGroupByGroupId = (productGroupId) =>
    this.props.quoteProductConfiguration.productGroups.find(x => x.id === productGroupId);

  updateQuote = (quote, configuration) => {
    const {
      quoteProductDto,
      onQuoteProductDtoChange,
      onloadSharedDocuments,
      onQuoteUpdated
    } = this.props;

    if (!quoteProductDto.productId && !quoteProductDto.inventoryItemId)
      return;

    updateQuotePlusLeadTimesAndProducts(
      quote,
      configuration,
      onQuoteUpdated,
      false,
      true,
      quoteProductDto,
      onQuoteProductDtoChange,
      this.context.onQuoteProductConfigurationUpdate,
      (newState) => this.setState(newState),
      onloadSharedDocuments,
      true
    );
  };

  render() {
    const {
      contact,
      quoteProductDto,
      submitting,
      productConfiguration,
      quoteProductConfiguration,
      couplerParNumberMade,
      onQuoteProductDtoChange,
      onSaveProduct,
      onContinue,
      onCancel,
      isEditProductMode,
      preparingForReview,
      isOrderFormMode,
      isAmiPriceLookupMode,
      hasAgreedStockTerms,
      onHasAgreedStockTermsChange,
      quote,
      documents,
      onloadSharedDocuments,
      showPricingSpinner,
      showLeadTimeCalculationSpinner,
      onProductGroupSelectionChange,
      selectedQuoteProductType,
      onOpenEdit,
      order,
      productSelected
    } = this.props;

    const {
      inventoryItems,
      inventoryItemId,
      selectedInventoryItem,
      highlightedInventoryItemId,
      highlightedInventoryItem
    } = this.state;

    const productCategories = quoteProductConfiguration.getProductCategoriesByAttachmentPointId(quoteProductDto.attachmentPointId, appConfig.isDealerApp);
    const productGroups = quoteProductConfiguration.getProductGroupsByProductCategoryId(quoteProductDto.productCategoryId, appConfig.isDealerApp, quoteProductDto.productGroupId);
    const productGroup = productGroups.find((x) => x.id === quoteProductDto.productGroupId);

    let machineMissing = true;
    let couplerMissing = true;

    if (isOrderFormMode && order) {
      const attachmentPoint = order.attachmentPoints.find((x) => quoteProductDto && quoteProductDto.attachmentPointId === x.id);
      const couplerParNumberMade = !!attachmentPoint && attachmentPoint.couplerDecision !== couplerDecision.notSpecified;

      couplerMissing = !couplerParNumberMade;
      machineMissing = !order.machineInfo.machineId;
    } else {
      if (selectedQuoteProductType === quoteProductType.inventoryOnly) {
        machineMissing = (!quote.step1 || !quote.step1.model || quote.step1.model === '');
        couplerMissing = !couplerParNumberMade;
      } else {
        machineMissing = productGroup && (!quote.step1 || !quote.step1.model || quote.step1.model === '');
        couplerMissing = productGroup && !couplerParNumberMade;
      }
    }

    const disabledAttachmentPointIds = quote.attachmentPoints
      .filter((x) => x.couplerDecision === null || x.couplerDecision === undefined)
      .map((x) => x.id);

    const showAttachmentPointSelection = quoteProductConfiguration.attachmentPoints.length !== 1 && !isEditProductMode;
    const showProductTypeSelection = !isAmiPriceLookupMode && (!showAttachmentPointSelection || (showAttachmentPointSelection && !!quoteProductDto.attachmentPointId));

    return (
      <Fragment>
        {!isOrderFormMode && (
          <InnerStepHeader
            step1={quote.step1}
            productConfiguration={productConfiguration}
            onContinue={onContinue}
            preparingForReview={preparingForReview}
            isAmiPriceLookupMode={isAmiPriceLookupMode}
            isFromStep3={!isEditProductMode && quote.id === null}
          />
        )}
        <div className="container step3-container">
          <AttachmentPointSelection
            show={showAttachmentPointSelection}
            attachmentPoints={quoteProductConfiguration.attachmentPoints}
            attachmentPointId={quoteProductDto.attachmentPointId}
            disabledAttachmentPointIds={disabledAttachmentPointIds}
            onChange={(attachmentPointId) => {
              onQuoteProductDtoChange({
                attachmentPointId,
                productCategoryId: null,
                productGroupId: null,
                productId: null,
                inventoryItemId: null,
                productOptions: [],
                deatil: null,
              });

              const inventoryItems = (quoteProductConfiguration.getAllInventoryItems() || [])
                .filter(x => x.attachmentPointId === attachmentPointId)
                .map((x) => new QuoteInventoryItemViewModel(x));

              this.setState({ inventoryItems: inventoryItems });
            }}
          />

          {showProductTypeSelection && (
            <div ref={(el) => (this.refToProductTypeSelectionComponent = el)}>
              <ProductTypeSelectionComponent
                selectedQuoteProductType={selectedQuoteProductType}
                onChange={(selectedType) => {
                  onProductGroupSelectionChange(selectedType);
                }}
              />
            </div>
          )}

          {selectedQuoteProductType === quoteProductType.inventoryOnly && !isEditProductMode && !isOrderFormMode && !isAmiPriceLookupMode && (
            <MachineInformationContainer
              quote={quote}
              quoteProductDto={quoteProductDto}
              isEditProductMode={isEditProductMode}
              onQuoteProductDtoChange={(inventoryItemId) =>
                onQuoteProductDtoChange({ inventoryItemId })
              }
              quoteProductConfiguration={quoteProductConfiguration}
              show={true}
              onloadSharedDocuments={onloadSharedDocuments}
              onSaved={async () => {
                const updatedQuote = this.props.quote.clone();
                const configuration = await QuoteProductConfiguration.getProductConfigurationFromQuote(updatedQuote);
                this.context.onQuoteProductConfigurationUpdate(configuration);

                this.updateQuote(updatedQuote, configuration);

                const inventoryItems = (configuration.inventoryItems || []).map((x) => new QuoteInventoryItemViewModel(x));
                this.setState({ inventoryItems: inventoryItems });
              }}
              isFromStep3InventoryOnly={true}
            />
          )}

          {selectedQuoteProductType === quoteProductType.inventoryOnly && !isAmiPriceLookupMode && (
            <InventoryItemTable
              products={quoteProductConfiguration.products}
              productGroups={quoteProductConfiguration.productGroups}
              inventoryItems={inventoryItems}
              selectedInventoryItem={selectedInventoryItem}
              productId={quoteProductDto.productId}
              inventoryItemId={quoteProductDto.inventoryItemId || inventoryItemId}
              onProductSelected={async (productId, inventoryItemId) => {
                const selectedProduct = this.getSelectedProduct(productId);
                const productGroupId = selectedProduct.productGroupId;
                const selectedProductGroup = this.getProductGroupByGroupId(productGroupId);
                const productGroup = quoteProductConfiguration.productGroups.find(x => x.id === productGroupId);
                const productCategoryId = productGroup.productCategoryIds?.length ? productGroup.productCategoryIds[0] : null;
                const visibleThumbConfigurator =
                  productGroup.type === ProductGroupTypes.StickPivotThumb.id ||
                  productGroup.type === ProductGroupTypes.ExtendableStickThumb.id ||
                  productGroup.type === ProductGroupTypes.ProLinkThumb.id ||
                  productGroup.type === ProductGroupTypes.WeldOnThumb.id;

                this.setState({ productId: productId, inventoryItemId: inventoryItemId, selectedInventoryItem: inventoryItems.find(x => x.id === inventoryItemId) || null });

                if (visibleThumbConfigurator) {
                  onQuoteProductDtoChange(
                    {
                      detail: null,
                      productId: null,
                      inventoryItemId: null,
                      productGroupId,
                      productOptions: [],
                      productCategoryId,
                      dimensions: QuoteProductDimension.getProductDimensions(selectedProductGroup, selectedProduct),
                    },
                    true
                  );
                }
                else {
                  onQuoteProductDtoChange(
                    {
                      detail: null,
                      productId,
                      inventoryItemId,
                      productGroupId,
                      productOptions: [],
                      productCategoryId,
                      dimensions: QuoteProductDimension.getProductDimensions(selectedProductGroup, selectedProduct),
                    },
                    true
                  );
                  onHasAgreedStockTermsChange(false);
                }
              }}
              onOpenEdit={onOpenEdit}
              viewAllProducts={() => {
                const node = findDOMNode(this.refToProductTypeSelectionComponent);
                if (node) {
                  node.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                  });

                  onProductGroupSelectionChange(quoteProductType.allProducts);
                }
              }}
              canSelectInventoryItem={(isEditProductMode || isOrderFormMode) ? true : (!machineMissing && !couplerMissing)}
              quote={quote}
              isEditProductMode={isEditProductMode}
              isOrderFormMode={isOrderFormMode}
              highlightedInventoryItemId={highlightedInventoryItemId}
              highlightedInventoryItem={highlightedInventoryItem}
            />
          )}

          {selectedQuoteProductType === quoteProductType.allProducts && !!quoteProductDto.attachmentPointId && !isEditProductMode && (
            <ProductCategorySelectionComponent
              contact={contact}
              attachmentPoint={quote.attachmentPoints.find(
                (x) => x.id === quoteProductDto.attachmentPointId
              )}
              productCategories={productCategories}
              productCategoryId={quoteProductDto.productCategoryId}
              selectedQuoteProductType={selectedQuoteProductType}
              onChange={(productCategoryId) => {
                onQuoteProductDtoChange({
                  productCategoryId,
                  productGroupId: null,
                  productId: null,
                  inventoryItemId: null,
                  productOptions: [],
                  deatil: null,
                });

                if (quoteProductDto.productCategoryId) {
                  this.refToProductGroupSelectionComponent.current.scrollToComponent();
                }
              }}
            />
          )}

          {
            selectedQuoteProductType === quoteProductType.allProducts && (
              <ProductGroupSelectionComponent
                ref={this.refToProductGroupSelectionComponent}
                show={!!quoteProductDto.productCategoryId && !isEditProductMode}
                scrollOnShow={true}
                productGroups={productGroups}
                productGroupId={quoteProductDto.productGroupId}
                couplerParNumberMade={couplerParNumberMade}
                onChange={(productGroupId) => {
                  onQuoteProductDtoChange(
                    {
                      productGroupId,
                      productId: null,
                      inventoryItemId: null,
                      productOptions: [],
                      detail: null,
                    },
                    true
                  );

                  if (quoteProductDto.productGroupId) {
                    this.refToProductAndOptionsSelectionComponent.current.scrollToComponent();
                  }
                }}
              />
            )
          }

          <ProductAndOptionsSelectionComponent
            ref={this.refToProductAndOptionsSelectionComponent}
            show={!!quoteProductDto.productGroupId}
            scrollOnShow={true}
            quoteProductDto={quoteProductDto}
            quoteProductConfiguration={quoteProductConfiguration}
            onQuoteProductDtoChange={onQuoteProductDtoChange}
            productGroup={productGroup || this.getProductGroupByGroupId(quoteProductDto.productGroupId)}
            submitting={submitting}
            onSaveProduct={onSaveProduct}
            onCancel={onCancel}
            productConfiguration={productConfiguration}
            isOrderFormMode={isOrderFormMode}
            machineMissing={machineMissing}
            couplerMissing={couplerMissing}
            isAmiPriceLookupMode={isAmiPriceLookupMode}
            hasAgreedStockTerms={hasAgreedStockTerms}
            onHasAgreedStockTermsChange={onHasAgreedStockTermsChange}
            quote={quote}
            documents={documents}
            onloadSharedDocuments={onloadSharedDocuments}
            showPricingSpinner={showPricingSpinner}
            showLeadTimeCalculationSpinner={showLeadTimeCalculationSpinner}
            selectedQuoteProductType={selectedQuoteProductType}
            selectedInventoryItem={selectedInventoryItem}
            productSelected={(productId, inventoryItemId)=>{
              this.setState({ selectedInventoryItem: null, selectedInventoryItemId: null });
              productSelected(productId, inventoryItemId);
            }}
          />
        </div>
      </Fragment>
    );
  }
}
